import type { SkillBand } from '../../shared/types/ids';
import {
  LAUNCH_IDENTITY_LANE_PLAYER_IDS,
  type LaunchIdentityRecord,
  type LaunchIdentitySeat,
} from '../../shared/types/launch-identity';
import {
  MISSION_SCORE_DETAIL_KEYS,
  type MissionProgressCounters,
  type MissionProgressSnapshot,
  type MissionScoreSnapshot,
} from '../../shared/types/mission-progress';
import { isGovernedId, isValidSkillId } from '../../shared/validation/skill-id';
import {
  MAX_LOCAL_PLAYERS,
  type PlayerLane,
  type PlayerMode,
  type PlayerRuntimeState,
} from '../../shared/types/players';
import type { ActivityLevel } from '../../shared/types/presentation';
import type {
  AccessibilityPreferences,
  ChildProfile,
  FamilyProfile,
  ParentSettings,
} from '../../shared/types/profiles';
import {
  APPLIED_SESSION_HISTORY_LIMIT,
  GAME_PROGRESS_COUNTER_KEYS,
  type AppliedSessionRecord,
  type GameProgressSnapshot,
  type GameRewardState,
} from '../../shared/types/progress';
import type {
  GameSessionSummary,
  PlayerParticipationRecord,
  SessionInputMode,
  SessionRecord,
} from '../../shared/types/session';
import type { SkillEstimate, SkillEvidence, SkillEvidenceResult } from '../../shared/types/skills';

/**
 * The persistence allowlist.
 *
 * Every value written to, or read back from, local storage is rebuilt field by field here.
 * Nothing reaches `JSON.stringify` because it happened to be attached to a caller's object —
 * TypeScript's structural typing means `{...validChild, poseLandmarks}` is still a valid
 * `ChildProfile` at the call site, so the runtime boundary has to be explicit.
 *
 * That makes architecture §19 enforceable rather than aspirational: video, camera frames,
 * pose landmarks, voice, biometrics and movement traces have no field to travel in, so they
 * are dropped whether the caller is careless or malicious.
 *
 * The same builders validate on the way back in. A record that no longer matches its shape
 * returns null, and the repository treats it as absent (PRO-02).
 */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function bool(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | null {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : null;
}

/** Adds `key` only when the value is present, to satisfy exactOptionalPropertyTypes. */
function withOptional<T extends object, K extends string, V>(
  base: T,
  key: K,
  value: V | null,
): T & Partial<Record<K, V>> {
  return value === null ? base : { ...base, [key]: value };
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : [];
}

/** An id this build could have generated, and short enough not to be carrying anything else. */
function governedId(value: unknown): string | null {
  const parsed = str(value);
  return parsed !== null && isGovernedId(parsed) ? parsed : null;
}

/** The same rule over a list: an entry that is not an id is not kept. */
function governedIdArray(value: unknown): string[] {
  return stringArray(value).filter((entry) => isGovernedId(entry));
}

/**
 * A numeric map whose *keys* are allowlisted too (R1-15).
 *
 * An open map is a field that accepts caller-controlled text, and D-028 already established that
 * object keys are as much a payload as object values are: `{"data:image/png;base64,…": 1,
 * poseX: 0.42, poseY: 0.13}` is a media marker and a coordinate series wearing a counter's
 * clothes. Anything the projector cannot name is not written and does not survive a read.
 */
function closedNumberRecord(
  value: unknown,
  allows: (key: string) => boolean,
): Record<string, number> {
  if (!isRecord(value)) return {};
  const projected: Record<string, number> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (!allows(key)) continue;
    const parsed = nonNegativeCount(entry);
    if (parsed !== null) projected[key] = parsed;
  }
  return projected;
}

/**
 * A count this build could have written: a whole number, not negative, inside safe-integer range.
 *
 * `num()` accepts every finite number, which is right for a confidence or a volume and wrong for
 * everything a mission counts. A structurally valid snapshot carrying `teamScore: -5` used to pass
 * the resume guard and put a negative shard total on a five-year-old's screen (R1-14, D-036(7)).
 */
function nonNegativeCount(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

/**
 * A count for a field whose contract is **absent, never zero** (P6R12-2, D-120(1)).
 *
 * `parentOverridesUsed` says how much help a grown-up gave, and it is written only when there was
 * some: absence means "this build, or this sitting, did not count", which is a different statement
 * from "nobody reached for the controls". `nonNegativeCount` cannot tell those apart, so an explicit
 * stored `0` — a shape this build's writer never produces — survived the round trip and came back as
 * a *counted* zero. Damage normalises down to silence here, exactly as it does for a negative or a
 * string.
 */
function positiveCount(value: unknown): number | null {
  const count = nonNegativeCount(value);
  return count === null || count === 0 ? null : count;
}

/**
 * A level this build could have written: a finite number inside the documented `0..1` contract.
 *
 * `num()` accepts every finite number, which is right for a timestamp-like quantity and wrong for
 * a gain. A stored `soundVolume: 4` used to survive the projector and reach
 * `resolveSessionLaunchDefaults`, where the gain node clamps it — so the record said one thing and
 * the room heard another, and the grown-up's screen would have shown the corrupt figure back to
 * them. Out-of-range is damage, and damage is dropped here like every other damaged field (R1-7).
 */
function unitInterval(value: unknown): number | null {
  const parsed = num(value);
  return parsed !== null && parsed >= 0 && parsed <= 1 ? parsed : null;
}

function projectAll<T>(value: unknown, project: (entry: unknown) => T | null): T[] {
  if (!Array.isArray(value)) return [];
  const projected: T[] = [];
  for (const entry of value) {
    const built = project(entry);
    if (built !== null) projected.push(built);
  }
  return projected;
}

const SKILL_BANDS = ['explorer', 'hero', 'master'] as const satisfies readonly SkillBand[];
const PLAYER_MODES = [
  'SINGLE',
  'COOP_SIMULTANEOUS_SIMPLE',
] as const satisfies readonly PlayerMode[];
const PLAYER_LANES = ['left', 'right'] as const satisfies readonly PlayerLane[];
const ACTIVITY_LEVELS = ['LOW', 'MEDIUM', 'HIGH'] as const satisfies readonly ActivityLevel[];
const INPUT_MODES = ['KEYBOARD', 'CAMERA', 'MIXED'] as const satisfies readonly SessionInputMode[];
const EVIDENCE_RESULTS = [
  'correct',
  'incorrect',
  'assisted',
  'skipped',
] as const satisfies readonly SkillEvidenceResult[];

export function toSkillEstimateDto(input: unknown): SkillEstimate | null {
  if (!isRecord(input)) return null;
  const skillId = str(input['skillId']);
  const confidence = num(input['confidence']);
  const level = num(input['level']);
  const evidenceCount = num(input['evidenceCount']);
  if (skillId === null || confidence === null || level === null || evidenceCount === null) {
    return null;
  }

  return withOptional(
    { skillId, confidence, level, evidenceCount },
    'lastPractisedAt',
    str(input['lastPractisedAt']),
  );
}

export function toSkillEvidenceDto(input: unknown): SkillEvidence | null {
  if (!isRecord(input)) return null;
  const playerId = governedId(input['playerId']);
  const gameId = governedId(input['gameId']);
  const missionId = governedId(input['missionId']);
  // A skill id has its own hierarchical shape (architecture §14). An evidence row filed against
  // anything else is not evidence about a skill, and the field is otherwise free text (S1-3).
  const skillId = str(input['skillId']);
  if (skillId !== null && !isValidSkillId(skillId)) return null;
  const result = oneOf(input['result'], EVIDENCE_RESULTS);
  const attemptNumber = num(input['attemptNumber']);
  const teachingUsed = bool(input['teachingUsed']);
  const recordedAt = str(input['recordedAt']);

  if (
    playerId === null ||
    gameId === null ||
    missionId === null ||
    skillId === null ||
    result === null ||
    attemptNumber === null ||
    teachingUsed === null ||
    recordedAt === null
  ) {
    return null;
  }

  const base: SkillEvidence = {
    playerId,
    gameId,
    missionId,
    skillId,
    result,
    attemptNumber,
    teachingUsed,
    recordedAt,
  };

  return withOptional(
    withOptional(base, 'responseTimeMs', num(input['responseTimeMs'])),
    'inputConfidence',
    num(input['inputConfidence']),
  );
}

function toRewardStateDto(input: unknown): GameRewardState {
  const record = isRecord(input) ? input : {};
  return withOptional(
    { unlockedRewardIds: stringArray(record['unlockedRewardIds']) },
    'badgeId',
    str(record['badgeId']),
  );
}

export function toGameProgressDto(input: unknown): GameProgressSnapshot | null {
  if (!isRecord(input)) return null;
  const gameId = governedId(input['gameId']);
  const playerId = governedId(input['playerId']);
  const schemaVersion = num(input['schemaVersion']);
  const updatedAt = str(input['updatedAt']);
  if (gameId === null || playerId === null || schemaVersion === null || updatedAt === null) {
    return null;
  }

  const appliedSessions = latestUniqueAppliedSessions(
    projectAll(input['appliedSessions'], toAppliedSessionDto),
  );

  return {
    gameId,
    playerId,
    schemaVersion,
    completedMissionIds: governedIdArray(input['completedMissionIds']),
    rewards: toRewardStateDto(input['rewards']),
    counters: closedNumberRecord(input['counters'], (key) =>
      (GAME_PROGRESS_COUNTER_KEYS as readonly string[]).includes(key),
    ),
    // Omitted rather than written empty, so progress from before this field existed round-trips
    // unchanged and an absent list keeps meaning "nothing has been applied yet" (R2-1).
    ...(appliedSessions.length === 0 ? {} : { appliedSessions }),
    updatedAt,
  };
}

/**
 * The per-session bookkeeping the progression transaction keeps (R2-1).
 *
 * Two named fields and nothing else: a session id and the team total it has contributed. There is
 * no timing here, no per-player figure and no ordering — it exists so one evening is applied once,
 * not so anything can be compared (architecture §12). The total is bounded like every other
 * persisted count, because a negative or fractional one cannot describe a mission this build ran.
 */
function toAppliedSessionDto(input: unknown): AppliedSessionRecord | null {
  if (!isRecord(input)) return null;
  const sessionId = governedId(input['sessionId']);
  const teamScore = nonNegativeCount(input['teamScore']);
  if (sessionId === null || teamScore === null) return null;
  return { sessionId, teamScore };
}

/**
 * One entry per session, and no more history than the writer would have kept (S1-12).
 *
 * The cap and the supersede-don't-accumulate rule are D-043(3)'s, and they were write-side rules
 * only: the projector preserved whatever was physically there. So a stored list holding session
 * `S` twice — once at the total it had contributed, once at an older one — let the runtime read
 * the *first* match and credit the difference all over again, and an over-long list quietly
 * widened the window in which that could happen. Made a projection rule here, which is the single
 * point every read passes through, so damaged or hand-edited history cannot overcredit a child's
 * progression.
 *
 * "Latest" is the last occurrence, because the writer appends: `[...without(sessionId), current]`.
 */
function latestUniqueAppliedSessions(
  entries: readonly AppliedSessionRecord[],
): AppliedSessionRecord[] {
  const latest = new Map<string, AppliedSessionRecord>();
  for (const entry of entries) {
    // Deleted before it is set, so the surviving entry also takes the *latest* position: a `Map`
    // otherwise keeps a re-set key where it was first inserted, and the cap below would then drop
    // the newest bookkeeping in favour of eight older records.
    latest.delete(entry.sessionId);
    latest.set(entry.sessionId, entry);
  }
  return [...latest.values()].slice(-APPLIED_SESSION_HISTORY_LIMIT);
}

function toAccessibilityDto(input: unknown): AccessibilityPreferences {
  const record = isRecord(input) ? input : {};
  return {
    reducedMotion: bool(record['reducedMotion']) ?? false,
    jumpSubstitute: bool(record['jumpSubstitute']) ?? false,
    narrationEnabled: bool(record['narrationEnabled']) ?? true,
    narrationVolume: num(record['narrationVolume']) ?? 1,
    highContrast: bool(record['highContrast']) ?? false,
  };
}

export function toChildProfileDto(input: unknown): ChildProfile | null {
  if (!isRecord(input)) return null;
  const id = str(input['id']);
  const nickname = str(input['nickname']);
  const band = oneOf(input['band'], SKILL_BANDS);
  const createdAt = str(input['createdAt']);
  const updatedAt = str(input['updatedAt']);
  if (
    id === null ||
    nickname === null ||
    band === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  const subjectEstimates: Record<string, SkillEstimate> = {};
  if (isRecord(input['subjectEstimates'])) {
    for (const [skillId, estimate] of Object.entries(input['subjectEstimates'])) {
      const projected = toSkillEstimateDto(estimate);
      if (projected !== null) subjectEstimates[skillId] = projected;
    }
  }

  const gameProgress: Record<string, GameProgressSnapshot> = {};
  if (isRecord(input['gameProgress'])) {
    for (const [gameId, progress] of Object.entries(input['gameProgress'])) {
      const projected = toGameProgressDto(progress);
      if (projected !== null) gameProgress[gameId] = projected;
    }
  }

  const base: ChildProfile = {
    id,
    nickname,
    band,
    accessibility: toAccessibilityDto(input['accessibility']),
    activityPreference: oneOf(input['activityPreference'], ACTIVITY_LEVELS) ?? 'MEDIUM',
    subjectEstimates,
    thriveAchievements: stringArray(input['thriveAchievements']),
    gameProgress,
    createdAt,
    updatedAt,
  };

  return withOptional(
    withOptional(
      withOptional(base, 'shortName', str(input['shortName'])),
      'ageBand',
      str(input['ageBand']),
    ),
    'avatarId',
    str(input['avatarId']),
  );
}

function toParentSettingsDto(input: unknown): ParentSettings {
  const record = isRecord(input) ? input : {};
  const base: ParentSettings = {
    defaultPlayerMode: oneOf(record['defaultPlayerMode'], PLAYER_MODES) ?? 'SINGLE',
    narrationEnabled: bool(record['narrationEnabled']) ?? true,
    narrationVolume: num(record['narrationVolume']) ?? 1,
    reducedMotion: bool(record['reducedMotion']) ?? false,
    cameraEnabled: bool(record['cameraEnabled']) ?? false,
    showParentSummaryAfterMission: bool(record['showParentSummaryAfterMission']) ?? true,
  };

  /**
   * Sound is carried **only when a grown-up actually chose it** (PS5-5).
   *
   * Every field above defaults here, because every one of them has been required since Phase 0 and
   * a missing one is damage. These two are different: they are optional, and a record written by
   * an earlier build is missing them because that build had nothing to say, not because anything
   * went wrong. Defaulting them here would write a preference nobody expressed into every existing
   * family's record on the next save — a migration by accident, which D-094(6) rules out. The
   * absence is resolved into a value exactly once, in `resolveSessionLaunchDefaults`.
   */
  return withOptional(
    withOptional(base, 'soundEnabled', bool(record['soundEnabled'])),
    'soundVolume',
    unitInterval(record['soundVolume']),
  );
}

export function toFamilyProfileDto(input: unknown): FamilyProfile | null {
  if (!isRecord(input)) return null;
  const id = str(input['id']);
  const createdAt = str(input['createdAt']);
  const updatedAt = str(input['updatedAt']);
  if (id === null || createdAt === null || updatedAt === null) return null;

  const base: FamilyProfile = {
    id,
    childProfileIds: stringArray(input['childProfileIds']),
    parentSettings: toParentSettingsDto(input['parentSettings']),
    createdAt,
    updatedAt,
  };

  return withOptional(base, 'displayName', str(input['displayName']));
}

/**
 * A seat, as it is *stored* (S1-1).
 *
 * `PlayerRuntimeState` carries a `shortName` because the stage has to put something above a lane,
 * and a parent may type a one-off name for an evening. That label is session furniture: it is not
 * a profile, nobody chose to save it, and D-054(2) is explicit that a sitting persists "profile
 * ids only, no names beyond what profiles already store". It used to travel into
 * `thrive.v1.sessions` and into the mission snapshot regardless, which put free text a grown-up
 * typed for one evening permanently on disk.
 *
 * So the projection drops it, in both directions: nothing writes it, and a record that already
 * holds one is rewritten clean by the same sanitising pass that removes any other field outside
 * the schema. Display is unaffected — the runtime keeps its own labels, and a restored sitting
 * re-derives them from the profile (D-055).
 */
function toPlayerRuntimeStateDto(input: unknown): PlayerRuntimeState | null {
  if (!isRecord(input)) return null;
  const playerId = governedId(input['playerId']);
  const lane = oneOf(input['lane'], PLAYER_LANES);
  const band = oneOf(input['band'], SKILL_BANDS);
  const isGuest = bool(input['isGuest']);
  const present = bool(input['present']);
  if (playerId === null || lane === null || band === null || isGuest === null || present === null) {
    return null;
  }

  return withOptional(
    { playerId, lane, band, isGuest, present },
    'profileId',
    governedId(input['profileId']),
  );
}

export function toSessionRecordDto(input: unknown): SessionRecord | null {
  if (!isRecord(input)) return null;
  const sessionId = governedId(input['sessionId']);
  const gameId = governedId(input['gameId']);
  const missionId = governedId(input['missionId']);
  const playerMode = oneOf(input['playerMode'], PLAYER_MODES);
  const inputMode = oneOf(input['inputMode'], INPUT_MODES);
  const startedAt = str(input['startedAt']);

  if (
    sessionId === null ||
    gameId === null ||
    missionId === null ||
    playerMode === null ||
    inputMode === null ||
    startedAt === null
  ) {
    return null;
  }

  const base: SessionRecord = {
    sessionId,
    gameId,
    missionId,
    playerMode,
    inputMode,
    players: projectAll(input['players'], toPlayerRuntimeStateDto),
    startedAt,
  };

  return withOptional(base, 'completedAt', str(input['completedAt']));
}

/**
 * Counters that count *rounds*, and are therefore bounded by the rounds that scored.
 *
 * Straight from the scoring strategy's semantics: every round event increments exactly one of
 * these and increments `roundsScored` in the same step, so in any state this build could produce
 * their total **equals** `roundsScored`. A stored `firstAttemptRounds: 12` beside
 * `roundsScored: 1` is not a mission that ran — and it is the difference between a child's badge
 * and a fabricated one, because the badge formula reads exactly these three (S1-11).
 */
const ROUND_TIER_DETAIL_KEYS = ['firstAttemptRounds', 'retryRounds', 'assistedRounds'] as const;

/**
 * Counters that count *shards*, and are therefore bounded by the team score.
 *
 * Same derivation: each applied event adds its award to `teamScore` and the identical award to
 * exactly one of these two, so their total equals `teamScore` in any faithful state.
 */
const SHARD_DETAIL_KEYS = ['shards', 'bonusShards'] as const;

/**
 * Spends a budget across named counters, in a fixed order, never rejecting the record.
 *
 * Normalising DOWN rather than refusing, on the R1-14/D-044(3) rule: a snapshot is a mission a
 * child is in the middle of, so a damaged summary counter must cost them a counter and never
 * twelve rounds of replay. Only a record that cannot describe *any* mission is refused outright,
 * and that judgement stays where it already is (`roundsScored > appliedEventIds.length`).
 */
function clampToBudget(
  detail: Record<string, number>,
  keys: readonly string[],
  budget: number,
): void {
  let remaining = budget;
  for (const key of keys) {
    const value = detail[key];
    if (value === undefined) continue;
    const allowed = Math.max(0, Math.min(value, remaining));
    remaining -= allowed;
    detail[key] = allowed;
  }
}

function toMissionScoreDto(
  input: unknown,
  playerIds: readonly string[],
): MissionScoreSnapshot | null {
  if (!isRecord(input)) return null;
  const teamScore = nonNegativeCount(input['teamScore']);
  const roundsScored = nonNegativeCount(input['roundsScored']);
  if (teamScore === null || roundsScored === null) return null;

  const appliedEventIds = governedIdArray(input['appliedEventIds']);
  // A round that scored left an applied event id behind it. Fewer ids than scored rounds is a
  // record that cannot describe a mission this build ran (R1-14).
  if (roundsScored > appliedEventIds.length) return null;

  const known = new Set(playerIds);
  const base: MissionScoreSnapshot = {
    teamScore,
    roundsScored,
    // Closed to the players the snapshot itself names: a participation count for somebody who is
    // not in this sitting is not a participation count.
    participation: closedNumberRecord(input['participation'], (key) => known.has(key)),
    appliedEventIds,
  };

  // `detail` is the game's own counters, so its keys are registered rather than open (R1-15) and
  // its values are bounded by the totals they are a breakdown of (S1-11).
  if (!isRecord(input['detail'])) return base;

  const detail = closedNumberRecord(input['detail'], (key) =>
    (MISSION_SCORE_DETAIL_KEYS as readonly string[]).includes(key),
  );
  clampToBudget(detail, ROUND_TIER_DETAIL_KEYS, roundsScored);
  clampToBudget(detail, SHARD_DETAIL_KEYS, teamScore);

  return { ...base, detail };
}

/**
 * Who launched the sitting (D-054(2), Slice R).
 *
 * Strict on every field, because this record's whole job is to let the resume guard decide whose
 * a saved mission is. A shape that only half survives is not a weaker identity, it is a different
 * one — so nothing here is defaulted, floored or repaired: the record is rebuilt exactly or it is
 * refused and the sitting starts fresh.
 *
 * The two refusals worth naming:
 *
 *   - a seat whose `playerId` is not the lane id for its index. Lanes are fixed for a whole
 *     mission (COOP-08) and the guard matches slot for slot, so a roster in the other order is
 *     not this sitting's roster;
 *   - the `isGuest`/`profileId` contradiction, in *either* direction. A guest has no stored
 *     profile by definition (D-025), and "not a guest, but nobody in particular" is precisely the
 *     identity a resume must never guess at.
 */
function toLaunchIdentitySeatDto(input: unknown, laneIndex: number): LaunchIdentitySeat | null {
  if (!isRecord(input)) return null;
  const laneId = LAUNCH_IDENTITY_LANE_PLAYER_IDS[laneIndex];
  const playerId = governedId(input['playerId']);
  const band = oneOf(input['band'], SKILL_BANDS);
  const isGuest = bool(input['isGuest']);
  if (playerId === null || playerId !== laneId || band === null || isGuest === null) return null;

  const profileId = governedId(input['profileId']);
  const profileCreatedAt = str(input['profileCreatedAt']);
  if (isGuest) {
    // A guest has no profile, so neither half of the profile claim may be present.
    return profileId === null && profileCreatedAt === null
      ? { playerId, band, isGuest: true }
      : null;
  }
  // A named seat carries both, or it is not a named seat: an id with no generation marker is the
  // pre-S1-8 shape, and a resume that cannot tell which generation it means is exactly the guess
  // this record exists to avoid. Refused, so the sitting starts fresh.
  return profileId === null || profileCreatedAt === null
    ? null
    : { playerId, band, isGuest: false, profileId, profileCreatedAt };
}

export function toLaunchIdentityDto(input: unknown): LaunchIdentityRecord | null {
  if (!isRecord(input)) return null;
  // The record's own game id is a governed id like every other one (S1-3): this projector is
  // strict on every field, and `str()` here was the one seam that still took arbitrary text.
  const gameId = governedId(input['gameId']);
  const playerMode = oneOf(input['playerMode'], PLAYER_MODES);
  const seats = input['players'];
  if (gameId === null || playerMode === null || !Array.isArray(seats)) return null;
  // One or two heroes. A sitting with nobody in it, or with a third, is not one this build ran.
  if (seats.length === 0 || seats.length > MAX_LOCAL_PLAYERS) return null;

  const players: LaunchIdentitySeat[] = [];
  for (const [index, seat] of seats.entries()) {
    const projected = toLaunchIdentitySeatDto(seat, index);
    // All or nothing: a roster missing a seat would be matched slot for slot against a snapshot
    // that still has it, which is a different sitting rather than a damaged one.
    if (projected === null) return null;
    players.push(projected);
  }

  return { gameId, playerMode, players };
}

/**
 * Counters are rebuilt as numbers with a zero floor rather than rejected: a snapshot whose
 * summary counts are damaged is still a resumable mission, and under-reporting one evening's
 * questions beats making a child replay twelve rounds.
 */
function toMissionCountersDto(input: unknown): MissionProgressCounters {
  const record = isRecord(input) ? input : {};
  return {
    questionsAttempted: nonNegativeCount(record['questionsAttempted']) ?? 0,
    questionsCompleted: nonNegativeCount(record['questionsCompleted']) ?? 0,
    retryCount: nonNegativeCount(record['retryCount']) ?? 0,
    teachingMomentCount: nonNegativeCount(record['teachingMomentCount']) ?? 0,
    participation: projectAll(record['participation'], toParticipationDto),
  };
}

/**
 * The interrupted-mission snapshot (PRO-03).
 *
 * Identity fields are required: a snapshot that cannot say which session, mission and round it
 * belongs to cannot be resumed, and resuming into a guess would be worse than starting again.
 */
export function toMissionProgressDto(input: unknown): MissionProgressSnapshot | null {
  if (!isRecord(input)) return null;
  const sessionId = governedId(input['sessionId']);
  const gameId = governedId(input['gameId']);
  const missionId = governedId(input['missionId']);
  const playerMode = oneOf(input['playerMode'], PLAYER_MODES);
  const inputMode = oneOf(input['inputMode'], INPUT_MODES);
  const stageId = governedId(input['stageId']);
  const roundIndex = nonNegativeCount(input['roundIndex']);
  const attemptNumber = nonNegativeCount(input['attemptNumber']);
  const players = projectAll(input['players'], toPlayerRuntimeStateDto);
  const score = toMissionScoreDto(
    input['score'],
    players.map((player) => player.playerId),
  );
  const startedAt = str(input['startedAt']);
  const updatedAt = str(input['updatedAt']);

  if (
    sessionId === null ||
    gameId === null ||
    missionId === null ||
    playerMode === null ||
    inputMode === null ||
    stageId === null ||
    roundIndex === null ||
    attemptNumber === null ||
    // There is no attempt zero: the first try is attempt one, and a resume that started the round
    // below it would be describing something that never happened.
    attemptNumber < 1 ||
    score === null ||
    // A mission cannot have scored more rounds than it has played (R1-14).
    score.roundsScored > roundIndex ||
    startedAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  /**
   * Closed to the players the snapshot itself names, exactly as participation is: an id for
   * somebody who is not in this sitting is not an observation of this sitting, and an open
   * string array is a field that accepts caller-controlled text (R1-15, D-028).
   *
   * And bounded by the attempt state (S1-10). Observations are only ever checkpointed by the miss
   * path, and that path increments the attempt in the same breath — so `attemptNumber: 1` is a
   * round on which nobody has yet answered, whatever the observation list claims. Honouring an
   * impossible pair let a resume plus a grown-up's Skip write durable `incorrect` against a child
   * for a response that never happened, which is precisely what D-044(3) forbids: damage
   * normalises DOWN, and can never fabricate a wrong answer.
   */
  const observedPlayerIds =
    attemptNumber > 1
      ? stringArray(input['observedPlayerIds']).filter((playerId) =>
          players.some((player) => player.playerId === playerId),
        )
      : [];

  return {
    sessionId,
    gameId,
    missionId,
    playerMode,
    inputMode,
    players,
    stageId,
    roundIndex,
    attemptNumber,
    onSimplified: bool(input['onSimplified']) ?? false,
    /**
     * Absent means "nothing observed yet", which is the state a fresh round is in. Reading a
     * damaged flag as `true` would fabricate a wrong answer nobody gave (R2-3).
     *
     * The two fields are one fact and have to agree (R3-2). "A wrong answer was seen" with nobody
     * left who is recorded as giving it is not a wrong answer — the ids are what say *whose* it
     * was, and evidence is only ever recorded for a confidently observed response (architecture
     * §12). So damage is normalised down to what survived, never up: the flag follows the
     * observations, and a resumed round with none is simply a round nobody has answered yet.
     */
    sawIncorrectAnswer:
      observedPlayerIds.length === 0 ? false : (bool(input['sawIncorrectAnswer']) ?? false),
    observedPlayerIds,
    ...(governedId(input['contentId']) === null
      ? {}
      : { contentId: governedId(input['contentId']) as string }),
    completedContentIds: governedIdArray(input['completedContentIds']),
    // The curated sequence is the default a fresh mission starts on, so a snapshot that does not
    // say otherwise is the curated one (D-037(1)).
    useCuratedSequence: bool(input['useCuratedSequence']) ?? true,
    score,
    progress: toMissionCountersDto(input['progress']),
    // Keys are hierarchical skill ids or nothing: `poseX` is not one, and neither is a data URL
    // (R1-15, architecture §14).
    skillPractice: closedNumberRecord(input['skillPractice'], isValidSkillId),
    // Closed to the snapshot's own players, exactly as participation and the observations are
    // (D-042(5), S1-3): a teaching cap spent by somebody who is not in this sitting is not a cap
    // this sitting spent, and the field is otherwise open text.
    teachingConsumedPlayerIds: stringArray(input['teachingConsumedPlayerIds']).filter((playerId) =>
      players.some((player) => player.playerId === playerId),
    ),
    /**
     * Absent stays absent (PS5-5, and exactly as the completed summary's field behaves).
     *
     * Defaulting a missing or damaged count to zero would turn "nobody counted" into "nobody
     * reached for the controls", which is a claim about a family's evening that the bytes do not
     * support — and a snapshot written by a build that never counted would come back asserting it.
     */
    ...(positiveCount(input['parentOverridesUsed']) === null
      ? {}
      : { parentOverridesUsed: positiveCount(input['parentOverridesUsed']) as number }),
    startedAt,
    updatedAt,
  };
}

function toParticipationDto(input: unknown): PlayerParticipationRecord | null {
  if (!isRecord(input)) return null;
  const playerId = str(input['playerId']);
  if (playerId === null) return null;

  return {
    playerId,
    responsesObserved: nonNegativeCount(input['responsesObserved']) ?? 0,
    participationWithinGrace: nonNegativeCount(input['participationWithinGrace']) ?? 0,
    bothRequiredCompletions: nonNegativeCount(input['bothRequiredCompletions']) ?? 0,
  };
}

export function toSessionSummaryDto(input: unknown): GameSessionSummary | null {
  if (!isRecord(input)) return null;
  const sessionId = str(input['sessionId']);
  const gameId = str(input['gameId']);
  const missionId = str(input['missionId']);
  const playerMode = oneOf(input['playerMode'], PLAYER_MODES);
  const completedAt = str(input['completedAt']);

  if (
    sessionId === null ||
    gameId === null ||
    missionId === null ||
    playerMode === null ||
    completedAt === null
  ) {
    return null;
  }

  return {
    sessionId,
    gameId,
    missionId,
    playerMode,
    questionsAttempted: num(input['questionsAttempted']) ?? 0,
    questionsCompleted: num(input['questionsCompleted']) ?? 0,
    retryCount: num(input['retryCount']) ?? 0,
    teachingMomentCount: num(input['teachingMomentCount']) ?? 0,
    teamScore: num(input['teamScore']) ?? 0,
    durationMs: num(input['durationMs']) ?? 0,
    participation: projectAll(input['participation'], toParticipationDto),
    /**
     * Absent stays absent (PS6-9, the PS5-5 pattern).
     *
     * Defaulting a missing count to zero would turn "nobody counted" into "nobody reached for the
     * controls", which is a claim about a family's evening that the bytes do not support.
     */
    ...(positiveCount(input['parentOverridesUsed']) === null
      ? {}
      : { parentOverridesUsed: positiveCount(input['parentOverridesUsed']) as number }),
    completedAt,
  };
}

/** Array projections used by the repositories on both read and write. */
export function toChildProfileListDto(input: unknown): ChildProfile[] {
  return projectAll(input, toChildProfileDto);
}

export function toSessionRecordListDto(input: unknown): SessionRecord[] {
  return projectAll(input, toSessionRecordDto);
}

export function toSkillEvidenceListDto(input: unknown): SkillEvidence[] {
  return projectAll(input, toSkillEvidenceDto);
}

/**
 * Keyed tables: an entry that fails projection is dropped, never partially written.
 *
 * `project` is handed the key as well as the entry, because in a durable-learning table the key
 * *is* an identity claim (S1-6). `learningSubjectIdOf` (D-052(3)) decides which child a row
 * belongs to on the way in, and the table's outer key is how it is found again — but nothing used
 * to check that the two still agreed, so damaged or hand-edited bytes could file child B's
 * evidence under child A's key and every read for A would serve B's learning. A projector that
 * cannot see the key it is filed under cannot notice.
 */
export function toKeyedDto<T>(
  input: unknown,
  project: (entry: unknown, key: string) => T | null,
): Record<string, T> {
  if (!isRecord(input)) return {};
  const projected: Record<string, T> = {};
  for (const [key, entry] of Object.entries(input)) {
    const built = project(entry, key);
    if (built !== null) projected[key] = built;
  }
  return projected;
}
